import { type ChangeEvent, useEffect, useRef, useState } from "react";

import { t } from "i18next";

import { Close } from "@mui/icons-material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Slider,
  Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material";

import { theme } from "@/src/theme/theme";

import {
  SavedSearchAtom,
  dealerActiveAuctionSearchFiltersAtom,
  dealerSavedSearchFiltersAtom
} from "@/src/state/dealer/dealerActiveAuctionSearchFiltersAtom";
import {
  VehicleType,
  dealerActiveVehicleFilterAtom
} from "@/src/state/dealer/dealerActiveVehicleFilterAtom";
import { notificationAtom } from "@/src/state/notificationAtom";

import CaravanIcon from "@/src/components/atoms/CaravanIcon";
import NumberRangeSelector from "@/src/components/molecules/NumberRangeSelector/NumberRangeSelector";
import DeleteSavedSearchConfirmationModal from "@/src/components/organisms/SearchFilterDrawer/DeleteSavedSearchConfirmationModal";
import SaveSearchModal from "@/src/components/organisms/SearchFilterDrawer/SaveSearchModal/SaveSearchModal";
import {
  RangeValue,
  getModelYearChipText,
  handleBikeTypeToggle,
  handleCaravanSubtypeToggle,
  handleCheckboxChange,
  handleDistanceChange,
  handleMakeChange,
  handleRangeChange,
  handleSearchFilterSelect,
  handleSellerDeliversChange,
  handleVehicleTypeToggle,
  hasNonDefaultValues
} from "@/src/components/organisms/SearchFilterDrawer/SearchFilterDrawer.lib";
import SearchFilterItem from "@/src/components/organisms/SearchFilterDrawer/SearchFilterItem/SearchFilterItem";

import VehicleCheckBox from "@/src/pages/dealer/components/VehicleCheckBox/VehicleCheckBox";

import { FUEL_TYPE_OPTIONS } from "@/src/constants/vehicle";
import {
  useGetBikeTypes,
  useGetFuelTypes,
  useGetGearTypes,
  useGetMakes
} from "@/src/queries/dealer/getSearchFilters";
import {
  CARAVAN_TYPE_CARAVAN,
  CARAVAN_TYPE_MOTORHOME,
  CARAVAN_TYPE_STORAGE_TRAILER
} from "@/src/utils/caravanTypes";
import { addThousandsSeparator } from "@/src/utils/numberUtils";

import { useAtom, useSetAtom } from "jotai";

interface SearchFilterDrawerProps {
  setOpenSearchFilter: (open: boolean) => void;
  isOpen: boolean;
  onClose: () => void;
  auctionsAmount?: number;
}

const SearchFilterDrawer = (props: SearchFilterDrawerProps) => {
  // Props
  const { setOpenSearchFilter, auctionsAmount, onClose, isOpen } = props;

  // UI State
  const isMobileView = useMediaQuery("(max-width:700px)");
  const gridSmValue = 12;
  const gridItemStyle = { display: "grid", gridAutoColumns: "1fr" };

  // Core State
  const [searchFilters, setSearchFilters] = useAtom(
    dealerActiveAuctionSearchFiltersAtom
  );
  const [selectedVehicles, setSelectedVehicles] = useAtom<VehicleType[]>(
    dealerActiveVehicleFilterAtom
  );
  const [savedSearchFilters, setSavedSearchFilters] = useAtom(
    dealerSavedSearchFiltersAtom
  );
  const setNotification = useSetAtom(notificationAtom);

  // Filter State
  const [hasClickedFilter, setHasClickedFilter] = useState(false);
  const [selectedSearchFilter, setSelectedSearchFilter] = useState("");
  const [isSaveButtonVisible, setIsSaveButtonVisible] = useState(false);
  const selectedVehicleType = searchFilters?.vehicleType ?? [
    VehicleType.CAR,
    VehicleType.MOTO,
    VehicleType.CARAVAN
  ];

  // Modal State
  const [isSaveSearchModalOpen, setIsSaveSearchModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [filterToBeDeleted, setFilterToBeDeleted] = useState("");

  // Data Fetching
  const { data: makes } = useGetMakes(selectedVehicleType);
  const { data: bikeTypes } = useGetBikeTypes();
  const { data: fuelTypesData } = useGetFuelTypes(
    selectedVehicleType,
    searchFilters ?? {}
  );
  const fuelTypes = [
    ...new Set([...(fuelTypesData ?? []), ...FUEL_TYPE_OPTIONS])
  ].sort();
  const { data: gearTypes } = useGetGearTypes(selectedVehicleType);

  // Constants
  const filterVehicleTypes = [
    VehicleType.CAR,
    VehicleType.MOTO,
    VehicleType.CARAVAN
  ];

  const getVehicleTypeLabel = (type: VehicleType): string => {
    switch (type) {
      case VehicleType.CAR:
        return t("dealer.searchFilters.vehicleType.car");
      case VehicleType.MOTO:
        return t("dealer.searchFilters.vehicleType.moto");
      case VehicleType.CARAVAN:
        return t("dealer.searchFilters.vehicleType.caravan");
      case VehicleType.ALL:
        return t("dealer.searchFilters.vehicleType.all");
      default:
        return "";
    }
  };

  const getVehicleTypeFromLabel = (label: string): VehicleType | undefined => {
    if (label === t("dealer.searchFilters.vehicleType.car"))
      return VehicleType.CAR;
    if (label === t("dealer.searchFilters.vehicleType.moto"))
      return VehicleType.MOTO;
    if (label === t("dealer.searchFilters.vehicleType.caravan"))
      return VehicleType.CARAVAN;
    return undefined;
  };
  const minYear = 1950;
  const maxYear = new Date().getFullYear() + 1;
  const minTargetPrice = 0;
  const maxTargetPrice = 100000;

  const handleVehicleTypeChange = (vehicleType: VehicleType) => {
    const next = handleVehicleTypeToggle(vehicleType, selectedVehicles);

    if (next === selectedVehicles) return;

    setSelectedVehicles(next);
    setSearchFilters((prev) => ({ ...(prev ?? {}), fuelType: [] }));
  };

  const handleBikeTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchFilters(handleBikeTypeToggle(event, searchFilters ?? {}));
    setIsSaveButtonVisible(true);
    setHasClickedFilter(false);
  };

  const handleCaravanSubtypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchFilters(handleCaravanSubtypeToggle(event, searchFilters ?? {}));
    setIsSaveButtonVisible(true);
    setHasClickedFilter(false);
  };

  const handleChangeMake = (event: SelectChangeEvent<string[]>) => {
    setSearchFilters(handleMakeChange(event, searchFilters ?? {}));
    setIsSaveButtonVisible(true);
    setHasClickedFilter(false);
  };

  const handleModelYearRangeChange = (range: RangeValue) => {
    setSearchFilters(
      handleRangeChange("modelYear", range, searchFilters ?? {})
    );
    setIsSaveButtonVisible(true);
    setHasClickedFilter(false);
  };

  const handleMileageRangeChange = (range: RangeValue) => {
    setSearchFilters(handleRangeChange("mileage", range, searchFilters ?? {}));
    setIsSaveButtonVisible(true);
    setHasClickedFilter(false);
  };

  const handleFuelTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchFilters(
      handleCheckboxChange("fuelType", event, searchFilters ?? {})
    );
    setIsSaveButtonVisible(true);
    setHasClickedFilter(false);
  };

  const handleGearTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchFilters(
      handleCheckboxChange("gearType", event, searchFilters ?? {})
    );
    setIsSaveButtonVisible(true);
    setHasClickedFilter(false);
  };

  const handleTargetPriceRangeChange = (range: RangeValue) => {
    setSearchFilters(
      handleRangeChange("targetPrice", range, searchFilters ?? {})
    );
    setIsSaveButtonVisible(true);
    setHasClickedFilter(false);
  };

  const handleChangeDistance = (event) => {
    setSearchFilters(handleDistanceChange(event, searchFilters ?? {}));
    setIsSaveButtonVisible(true);
    setHasClickedFilter(false);
  };

  const handleChangeSellerDelivers = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchFilters(handleSellerDeliversChange(event, searchFilters ?? {}));
    setIsSaveButtonVisible(true);
    setHasClickedFilter(false);
  };

  const getSelectedModelYearChipText = () =>
    getModelYearChipText(searchFilters?.modelYear);

  const handleSelectSearchFilter = (selectedFilterName: string) => {
    const { filters, newFilterName } = handleSearchFilterSelect(
      selectedFilterName,
      selectedSearchFilter,
      savedSearchFilters
    );

    setSearchFilters(filters);
    setSelectedSearchFilter(newFilterName);
    setHasClickedFilter(!!newFilterName);
  };

  const handleNewFilterSaved = (newFilterName: string) => {
    setSelectedSearchFilter(newFilterName);
  };

  const handleUpdateFilter = () => {
    setIsSaveButtonVisible(false);
    const selectedFilter = savedSearchFilters?.find(
      (filter) => filter.name === selectedSearchFilter
    );

    if (selectedFilter) {
      setSavedSearchFilters((prevFilters) => {
        const updatedFilters = prevFilters?.map((filter) =>
          filter.name === selectedSearchFilter
            ? { ...filter, searchFilters: searchFilters }
            : filter
        ) as SavedSearchAtom[];

        setTimeout(() => {
          setNotification({
            text: t(
              "dealer.searchFilterMessages.searchFiltersUpdatedSuccessfully"
            ),
            variant: "info"
          });
        }, 150);
        return updatedFilters;
      });
    }
  };

  const handleDeleteSavedSearchFilter = (filterName: string) => {
    setIsDeleteDialogOpen(false);

    const updatedFilters = savedSearchFilters?.filter(
      (filter) => filter.name !== filterName
    );
    setSavedSearchFilters(updatedFilters);
    setTimeout(() => {
      setNotification({
        text: t("dealer.searchFilterMessages.searchFilterDeletedSuccessfully"),
        variant: "success"
      });
    }, 150);
  };

  const getTargetPriceChipText = (start?: number, end?: number) => {
    const rangeMinText = `${addThousandsSeparator(start)} €`;
    const rangeMaxText = `${
      end
        ? addThousandsSeparator(end)
        : `${addThousandsSeparator(maxTargetPrice)}+ €`
    }`;
    return `${rangeMinText} - ${rangeMaxText}`;
  };

  useEffect(() => {
    const selectedFilter = localStorage.getItem("selectedSearchFilter");
    setSelectedSearchFilter(selectedFilter ?? "");
  }, []);

  useEffect(() => {
    if (selectedSearchFilter !== undefined && selectedSearchFilter !== null) {
      localStorage.setItem("selectedSearchFilter", selectedSearchFilter);
    }
  }, [selectedSearchFilter]);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Update search filters when vehicle type changes (not on initial mount)
    setSearchFilters((prev) => ({
      ...prev,
      vehicleType: selectedVehicles,
      // Reset make when vehicle type changes since available makes will change
      make: []
    }));
  }, [selectedVehicles, setSearchFilters]);

  const vehicleTypeGrid = (
    <Grid
      sx={gridItemStyle}
      size={{
        sm: gridSmValue,
        xs: 12
      }}
    >
      <SearchFilterItem
        data-testid="dealer-filter-vehicle-type"
        title={t("dealer.searchFilters.vehicleType.title")}
        selectedValues={searchFilters?.vehicleType?.map((type) =>
          getVehicleTypeLabel(type)
        )}
        handleDelete={(labelToRemove) => {
          const typeToRemove = getVehicleTypeFromLabel(labelToRemove);
          if (
            !typeToRemove ||
            !searchFilters?.vehicleType ||
            searchFilters.vehicleType.length === 1
          ) {
            return;
          }
          setSearchFilters({
            ...searchFilters,
            vehicleType: searchFilters.vehicleType.filter(
              (vt) => vt !== typeToRemove
            )
          });
          setSelectedVehicles((prev) =>
            prev.filter((vt) => vt !== typeToRemove)
          );
          setIsSaveButtonVisible(true);
          setHasClickedFilter(false);
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <VehicleCheckBox
            label={t("dealer.searchFilters.vehicleType.all")}
            value={VehicleType.ALL}
            isChecked={filterVehicleTypes.every((vehicleType) =>
              selectedVehicles.includes(vehicleType)
            )}
            onToggle={handleVehicleTypeChange}
          />
          <VehicleCheckBox
            label={t("dealer.searchFilters.vehicleType.car")}
            icon={<DirectionsCarIcon />}
            value={VehicleType.CAR}
            isChecked={
              searchFilters?.vehicleType?.length === 1 &&
              searchFilters?.vehicleType?.[0] === VehicleType.CAR
            }
            onToggle={handleVehicleTypeChange}
          />
          <VehicleCheckBox
            label={t("dealer.searchFilters.vehicleType.moto")}
            icon={<TwoWheelerIcon />}
            value={VehicleType.MOTO}
            isChecked={
              searchFilters?.vehicleType?.length === 1 &&
              searchFilters?.vehicleType?.[0] === VehicleType.MOTO
            }
            onToggle={handleVehicleTypeChange}
          />
          <VehicleCheckBox
            label={t("dealer.searchFilters.vehicleType.caravan")}
            icon={<CaravanIcon />}
            value={VehicleType.CARAVAN}
            isChecked={
              searchFilters?.vehicleType?.length === 1 &&
              searchFilters?.vehicleType?.[0] === VehicleType.CARAVAN
            }
            onToggle={handleVehicleTypeChange}
          />
        </Box>
      </SearchFilterItem>
    </Grid>
  );

  const bikeTypeFilterGrid = (
    <Grid size={12}>
      <SearchFilterItem
        data-testid="dealer-filter-bike-type"
        title={t("dealer.searchFilters.bikeType.title")}
        selectedValues={searchFilters?.bikeType}
        handleDelete={(selectedBikeType) => {
          setSearchFilters({
            ...searchFilters,
            bikeType: searchFilters?.bikeType?.filter(
              (bikeType) => bikeType !== selectedBikeType
            )
          });
          setIsSaveButtonVisible(true);
          setHasClickedFilter(false);
        }}
      >
        <FormControl component="fieldset" variant="standard" name="bikeType">
          <FormGroup>
            {!bikeTypes || bikeTypes.length === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  py: 1,
                  px: 0.5,
                  fontStyle: "italic"
                }}
              >
                {t("dealer.searchFilters.bikeType.noBikeTypesAvailable")}
              </Typography>
            ) : (
              <Grid container sx={{ width: ["108%", "100%"] }}>
                {bikeTypes.map((bikeType) => (
                  <Grid key={`bike-type-${bikeType}`} size={6}>
                    <FormControlLabel
                      sx={{ pl: 0.5 }}
                      control={
                        <Checkbox
                          sx={{ p: 0.5 }}
                          size="small"
                          checked={
                            bikeType && searchFilters?.bikeType
                              ? searchFilters.bikeType?.includes(bikeType)
                              : false
                          }
                          value={bikeType}
                          name="bikeType"
                          onChange={handleBikeTypeChange}
                        />
                      }
                      label={bikeType}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </FormGroup>
        </FormControl>
      </SearchFilterItem>
    </Grid>
  );

  const caravanTypes = [
    CARAVAN_TYPE_MOTORHOME,
    CARAVAN_TYPE_CARAVAN,
    CARAVAN_TYPE_STORAGE_TRAILER
  ];

  const onlyNonMotorhomeCaravansSelected =
    (searchFilters?.caravanSubtype?.length ?? 0) > 0 &&
    !searchFilters?.caravanSubtype?.includes(CARAVAN_TYPE_MOTORHOME);

  const caravanTypeFilterGrid = (
    <Grid size={12}>
      <SearchFilterItem
        data-testid="dealer-filter-caravan-type"
        title={t("dealer.searchFilters.caravanType.title")}
        selectedValues={searchFilters?.caravanSubtype}
        handleDelete={(selectedSubtype) => {
          setSearchFilters({
            ...searchFilters,
            caravanSubtype: searchFilters?.caravanSubtype?.filter(
              (subtype) => subtype !== selectedSubtype
            )
          });
          setIsSaveButtonVisible(true);
          setHasClickedFilter(false);
        }}
      >
        <FormControl
          component="fieldset"
          variant="standard"
          name="caravanSubtype"
        >
          <FormGroup>
            <Grid container sx={{ width: ["108%", "100%"] }}>
              {caravanTypes.map((caravanType) => (
                <Grid key={`caravan-type-${caravanType}`} size={6}>
                  <FormControlLabel
                    sx={{ pl: 0.5 }}
                    control={
                      <Checkbox
                        sx={{ p: 0.5 }}
                        size="small"
                        checked={
                          searchFilters?.caravanSubtype
                            ? searchFilters.caravanSubtype.includes(caravanType)
                            : false
                        }
                        value={caravanType}
                        name="caravanSubtype"
                        onChange={handleCaravanSubtypeChange}
                      />
                    }
                    label={caravanType}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>
        </FormControl>
      </SearchFilterItem>
    </Grid>
  );

  const makeFilterGrid = (
    <Grid
      size={{
        sm: gridSmValue,
        xs: 12
      }}
    >
      <SearchFilterItem
        data-testid="dealer-filter-make"
        title={t("dealer.searchFilters.make")}
        selectedValues={searchFilters?.make}
        handleDelete={(selectedMake) => {
          setSearchFilters({
            ...searchFilters,
            make: searchFilters?.make?.filter((make) => make !== selectedMake)
          });
          setIsSaveButtonVisible(true);
          setHasClickedFilter(false);
        }}
        showSelectedValues
      >
        <FormControl
          sx={{ width: "100%", maxWidth: ["16rem", "20rem"] }}
          size="small"
        >
          <InputLabel id="make-select-label">
            {t("dealer.searchFilters.make")}
          </InputLabel>
          <Select
            labelId="make-select-label"
            id="make-select"
            data-testid="dealer-filter-make-select"
            multiple
            value={searchFilters?.make ? searchFilters?.make : []}
            onChange={handleChangeMake}
            input={<OutlinedInput label="Merkki" />}
            renderValue={(selected) => selected.join(", ")}
            MenuProps={{
              slotProps: {
                paper: {
                  style: {
                    maxHeight: 400
                  }
                }
              }
            }}
          >
            {makes &&
              [...makes]
                .sort((a, b) => a.localeCompare(b))
                .map((make) => (
                  <MenuItem
                    key={`make-${make}`}
                    value={make}
                    sx={{ height: "2.25rem" }}
                  >
                    <Checkbox
                      checked={
                        make && searchFilters?.make
                          ? searchFilters.make?.includes(make)
                          : false
                      }
                    />
                    <ListItemText primary={make} />
                  </MenuItem>
                ))}
          </Select>
        </FormControl>
      </SearchFilterItem>
    </Grid>
  );

  const modelYearGrid = (
    <Grid
      size={{
        sm: gridSmValue,
        xs: 12
      }}
    >
      <SearchFilterItem
        data-testid="dealer-filter-year"
        title={t("dealer.searchFilters.modelYear")}
        selectedValues={getSelectedModelYearChipText()}
        handleDelete={() => {
          setSearchFilters({
            ...searchFilters,
            modelYear: undefined
          });
          setIsSaveButtonVisible(true);
          setHasClickedFilter(false);
        }}
      >
        <NumberRangeSelector
          startValue={
            searchFilters?.modelYear === undefined
              ? minYear
              : searchFilters.modelYear.start
          }
          endValue={
            searchFilters?.modelYear === undefined
              ? maxYear
              : searchFilters.modelYear.end
          }
          minValue={minYear}
          maxValue={maxYear}
          showLessThanPrefix
          maxLength={4}
          onRangeChange={handleModelYearRangeChange}
        />
      </SearchFilterItem>
    </Grid>
  );

  const mileageGrid = (
    <Grid
      size={{
        sm: gridSmValue,
        xs: 12
      }}
    >
      <SearchFilterItem
        data-testid="dealer-filter-mileage"
        title={t("dealer.searchFilters.mileage")}
        selectedValues={
          searchFilters?.mileage?.start
            ? [
                `${addThousandsSeparator(searchFilters.mileage.start)} - ${
                  searchFilters.mileage.end
                    ? addThousandsSeparator(searchFilters.mileage.end)
                    : addThousandsSeparator(250000) + "+"
                } km`
              ]
            : undefined
        }
        handleDelete={() => {
          setSearchFilters({
            ...searchFilters,
            mileage: undefined
          });
          setIsSaveButtonVisible(true);
          setHasClickedFilter(false);
        }}
      >
        <NumberRangeSelector
          startValue={
            searchFilters?.mileage === undefined
              ? 0
              : searchFilters.mileage.start
          }
          endValue={
            searchFilters?.mileage === undefined
              ? 250000
              : searchFilters.mileage.end
          }
          minValue={0}
          maxValue={250000}
          maxLength={6}
          onRangeChange={handleMileageRangeChange}
        />
      </SearchFilterItem>
    </Grid>
  );

  const powerTypeGrid = (
    <Grid size={12} sx={{ mb: 1.5 }}>
      <SearchFilterItem
        data-testid="dealer-filter-fueltype"
        title={t("dealer.auctionDetailsPage.powerType")}
        selectedValues={searchFilters?.fuelType}
        handleDelete={(selectedFuelType) => {
          setSearchFilters({
            ...searchFilters,
            fuelType: searchFilters?.fuelType?.filter(
              (fuelType) => fuelType !== selectedFuelType
            )
          });
          setIsSaveButtonVisible(true);
          setHasClickedFilter(false);
        }}
      >
        <FormControl component="fieldset" variant="standard" name="fuelType">
          <FormGroup>
            <Grid container>
              {fuelTypes?.map((fuel) => {
                const needsTooltip =
                  fuel &&
                  fuel.length > 11 &&
                  !selectedVehicleType?.includes(VehicleType.CARAVAN);

                return (
                  <Grid key={`fuel-type-${fuel}`} size={6}>
                    {needsTooltip ? (
                      <Tooltip title={fuel} placement="top" arrow>
                        <FormControlLabel
                          sx={{
                            pl: 0.5,
                            width: "100%",
                            ...(selectedVehicleType?.includes(
                              VehicleType.CARAVAN
                            )
                              ? { whiteSpace: "normal" }
                              : {
                                  textWrap: "nowrap",
                                  span: {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                  }
                                })
                          }}
                          control={
                            <Checkbox
                              sx={{
                                p: 0.5
                              }}
                              size="small"
                              checked={
                                fuel && searchFilters?.fuelType
                                  ? searchFilters.fuelType?.includes(fuel)
                                  : false
                              }
                              value={fuel}
                              name="fuelType"
                              onChange={handleFuelTypeChange}
                            />
                          }
                          label={fuel}
                        />
                      </Tooltip>
                    ) : (
                      <FormControlLabel
                        sx={{
                          pl: 0.5,
                          width: "100%",
                          ...(selectedVehicleType?.includes(VehicleType.CARAVAN)
                            ? { whiteSpace: "normal" }
                            : {
                                textWrap: "nowrap",
                                span: {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis"
                                }
                              })
                        }}
                        control={
                          <Checkbox
                            sx={{
                              p: 0.5
                            }}
                            size="small"
                            checked={
                              fuel && searchFilters?.fuelType
                                ? searchFilters.fuelType?.includes(fuel)
                                : false
                            }
                            value={fuel}
                            name="fuelType"
                            onChange={handleFuelTypeChange}
                          />
                        }
                        label={fuel}
                      />
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </FormGroup>
        </FormControl>
      </SearchFilterItem>
    </Grid>
  );

  const locationGrid = (
    <Grid
      sx={gridItemStyle}
      size={{
        sm: gridSmValue,
        xs: 12
      }}
    >
      <SearchFilterItem
        data-testid="dealer-filter-location"
        title={t("dealer.searchFilters.location")}
        selectedValues={
          searchFilters?.distance !== 0 && !!searchFilters?.distance
            ? [searchFilters?.distance + " km"]
            : undefined
        }
        handleDelete={() => {
          if (searchFilters) {
            const {
              distance: _distance,
              sellerDelivers: _sellerDelivers,
              ...filters
            } = searchFilters;
            setSearchFilters(filters);
          }
          setIsSaveButtonVisible(true);
          setHasClickedFilter(false);
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.5,
            pl: 2,
            mb: 0.5
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {searchFilters?.distance ? "Enintään" : "Ei etäisyysrajoitusta"}
          </Typography>
          {!!searchFilters?.distance && (
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: "primary.main" }}
            >
              {searchFilters.distance} km
            </Typography>
          )}
        </Box>
        <Slider
          sx={{
            mx: 2,
            mb: 2,
            width: "90%",
            "& .MuiSlider-mark": {
              display: "none"
            },
            "& .MuiSlider-markLabel": {
              fontSize: "0.7rem",
              color: "text.secondary"
            },
            "& .MuiSlider-markLabel[data-index='4']": {
              transform: "translateX(-90%)"
            }
          }}
          data-testid="dealer-filter-location-slider"
          aria-label="Delivery range"
          value={searchFilters?.distance ? searchFilters?.distance : 0}
          min={0}
          max={600}
          step={null}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) =>
            v === 600 ? "1000 km" : v === 0 ? "Ei rajaa" : `${v} km`
          }
          marks={marks}
          onChange={handleChangeDistance}
        />
        <FormControlLabel
          sx={{ pl: 1, mt: 0.5, alignItems: "center" }}
          control={
            <Checkbox
              sx={{ p: 0.5 }}
              size="small"
              checked={
                typeof searchFilters?.sellerDelivers === "undefined"
                  ? true
                  : searchFilters?.sellerDelivers
              }
              disabled={!searchFilters?.distance}
              value="deliver"
              name="canDeliver"
              onChange={handleChangeSellerDelivers}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("dealer.searchFilters.sellerDelivers")}
            </Typography>
          }
        />
      </SearchFilterItem>
    </Grid>
  );

  const targetPriceGrid = (
    <Grid
      size={{
        sm: gridSmValue,
        xs: 12
      }}
    >
      <SearchFilterItem
        data-testid="dealer-filter-price"
        title={t("dealer.searchFilters.targetPrice")}
        selectedValues={
          searchFilters?.targetPrice
            ? [
                getTargetPriceChipText(
                  searchFilters.targetPrice.start,
                  searchFilters.targetPrice.end
                )
              ]
            : undefined
        }
        handleDelete={() => {
          setSearchFilters({
            ...searchFilters,
            targetPrice: undefined
          });
          setIsSaveButtonVisible(true);
          setHasClickedFilter(false);
        }}
      >
        <NumberRangeSelector
          startValue={
            searchFilters?.targetPrice === undefined
              ? minTargetPrice
              : searchFilters.targetPrice.start
          }
          endValue={
            searchFilters?.targetPrice === undefined
              ? maxTargetPrice
              : searchFilters.targetPrice.end
          }
          minValue={minTargetPrice}
          maxValue={maxTargetPrice}
          maxLength={6}
          onRangeChange={handleTargetPriceRangeChange}
        />
      </SearchFilterItem>
    </Grid>
  );

  const transmissionTypeGrid = (
    <Grid
      sx={gridItemStyle}
      size={{
        sm: gridSmValue,
        xs: 12
      }}
    >
      <SearchFilterItem
        data-testid="dealer-filter-transmission"
        title={t("dealer.auctionDetailsPage.transmissionType")}
        selectedValues={searchFilters?.gearType}
        handleDelete={(selectedGearType) => {
          setSearchFilters({
            ...searchFilters,
            gearType: searchFilters?.gearType?.filter(
              (gearType) => gearType !== selectedGearType
            )
          });
          setIsSaveButtonVisible(true);
          setHasClickedFilter(false);
        }}
      >
        <FormControl component="fieldset" variant="standard" name="gearType">
          <FormGroup>
            <Grid container>
              {gearTypes?.map((gearType) => (
                <Grid key={`gear-type-${gearType}`} size={12}>
                  <FormControlLabel
                    sx={{ pl: 0.5 }}
                    control={
                      <Checkbox
                        sx={{ p: 0.5 }}
                        size="small"
                        checked={
                          gearType && searchFilters?.gearType
                            ? searchFilters.gearType?.includes(gearType)
                            : false
                        }
                        value={gearType}
                        name="gearType"
                        onChange={handleGearTypeChange}
                      />
                    }
                    label={gearType}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>
        </FormControl>
      </SearchFilterItem>
    </Grid>
  );

  const resetFilters = () => {
    setSearchFilters({
      vehicleType: filterVehicleTypes
    });
    setSelectedSearchFilter("");
    setSelectedVehicles(filterVehicleTypes);
  };

  const hasCustomFilters = hasNonDefaultValues(searchFilters);

  const actionButtonsGrid = (
    <Grid
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: theme.palette.background.paper,
        borderTop: "1px solid",
        borderColor: theme.palette.divider,
        zIndex: 10,
        width: ["80vw", "360px"],
        px: 2
      }}
      size={{
        sm: 12,
        xs: 12
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          margin: "0 auto",
          gap: 1,
          my: 1
        }}
      >
        {selectedSearchFilter.length > 0 &&
        !hasClickedFilter &&
        isSaveButtonVisible ? (
          <Button
            id="updateSearchButton"
            data-testid="dealer-filter-apply-btn"
            variant="contained"
            color="primary"
            onClick={handleUpdateFilter}
            sx={(theme) => ({ fontSize: theme.typography.caption.fontSize })}
          >
            {t("dealer.searchFilters.actionButtons.updateSearch")} (
            {auctionsAmount})
          </Button>
        ) : (
          <Button
            id="updateSearchButton"
            data-testid="dealer-filter-apply-btn"
            variant="contained"
            color="primary"
            onClick={() => setOpenSearchFilter(false)}
          >
            {t("dealer.searchFilters.actionButtons.showResults")} (
            {auctionsAmount})
          </Button>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%"
          }}
        >
          <IconButton
            id="saveSearchButton"
            color="primary"
            size="small"
            onClick={() => setIsSaveSearchModalOpen(true)}
            disabled={!hasCustomFilters}
            sx={{
              opacity: hasCustomFilters ? 1 : 0.5
            }}
          >
            <BookmarkBorderIcon
              color="secondary"
              sx={{ fontSize: (theme) => theme.typography.subtitle1.fontSize }}
            />
            <Typography
              variant="body2"
              color="secondary"
              sx={{ p: { cursor: hasCustomFilters ? "pointer" : "default" } }}
            >
              {t("dealer.searchFilterGeneral.saveFilter")}
            </Typography>
          </IconButton>

          <IconButton
            id="clearSearchButton"
            data-testid="dealer-filter-clear-btn"
            color="primary"
            size="small"
            onClick={resetFilters}
            disabled={!hasCustomFilters}
            sx={{
              opacity: hasCustomFilters ? 1 : 0.5
            }}
          >
            <CloseIcon
              color="secondary"
              sx={{ fontSize: (theme) => theme.typography.subtitle1.fontSize }}
            />
            <Typography
              variant="body2"
              color="secondary"
              sx={{ p: { cursor: hasCustomFilters ? "pointer" : "default" } }}
            >
              {t("dealer.searchFilters.actionButtons.clearSearch")}
            </Typography>
          </IconButton>
        </Box>
        {searchFilters && (
          <SaveSearchModal
            isOpen={isSaveSearchModalOpen}
            searchFilters={searchFilters}
            onClose={() => {
              setIsSaveSearchModalOpen(false);
            }}
            onSave={handleNewFilterSaved}
          />
        )}
      </Box>
    </Grid>
  );

  return (
    <Drawer open={isOpen} onClose={() => setOpenSearchFilter(false)}>
      <Box sx={{ width: ["80vw", "360px"], height: "100%", p: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Typography variant="h2" sx={{ pb: 0, mb: 0 }}>
            {t("dealer.searchFilters.filterTitle")}
          </Typography>
          <IconButton
            sx={{ m: 0, p: 0 }}
            onClick={onClose}
            aria-label="close drawer"
          >
            <Close />
          </IconButton>
        </Box>

        {savedSearchFilters && savedSearchFilters.length > 0 && (
          <Box>
            <Typography variant="h6">
              {t("dealer.searchFilterGeneral.savedFilters")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                mt: 2
              }}
            >
              <Grid container spacing={1}>
                {savedSearchFilters.map((item) => (
                  <Grid key={`saved-filter-${item.name}`}>
                    <Chip
                      title="poista haku"
                      label={item.name}
                      variant={
                        selectedSearchFilter === item.name
                          ? "filled"
                          : "outlined"
                      }
                      sx={{
                        color:
                          selectedSearchFilter === item.name
                            ? theme.palette.semantic.primary.contrastText
                            : theme.palette.common.black,
                        maxWidth: isMobileView ? "350px" : "100%",
                        wordBreak: "break-word"
                      }}
                      color="primary"
                      onClick={() => handleSelectSearchFilter(item.name)}
                      onDelete={() => {
                        setFilterToBeDeleted(item.name);
                        setIsDeleteDialogOpen(true);
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
              <DeleteSavedSearchConfirmationModal
                open={isDeleteDialogOpen}
                filterName={filterToBeDeleted}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleDeleteSavedSearchFilter}
              />
            </Box>
          </Box>
        )}
        <Grid container sx={{ pb: [15, 14] }}>
          {vehicleTypeGrid}
          {searchFilters?.vehicleType?.includes(VehicleType.MOTO) &&
            bikeTypeFilterGrid}
          {searchFilters?.vehicleType?.includes(VehicleType.CARAVAN) &&
            caravanTypeFilterGrid}
          {makeFilterGrid}
          {modelYearGrid}
          {!onlyNonMotorhomeCaravansSelected && mileageGrid}
          {!onlyNonMotorhomeCaravansSelected && powerTypeGrid}
          {!onlyNonMotorhomeCaravansSelected && transmissionTypeGrid}
          {targetPriceGrid}
          {locationGrid}
          {actionButtonsGrid}
        </Grid>
      </Box>
    </Drawer>
  );
};

export default SearchFilterDrawer;

const marks = [
  {
    value: 0,
    label: ""
  },
  {
    value: 10,
    label: "10 km"
  },
  {
    value: 20
  },
  {
    value: 30
  },
  {
    value: 40
  },
  {
    value: 50
  },
  {
    value: 60
  },
  {
    value: 70
  },
  {
    value: 80
  },
  {
    value: 90
  },
  {
    value: 100
  },
  {
    value: 150,
    label: "150 km"
  },
  {
    value: 200
  },
  {
    value: 250
  },
  {
    value: 300,
    label: "300 km"
  },
  {
    value: 350
  },
  {
    value: 400
  },
  {
    value: 450,
    label: "450 km"
  },
  {
    value: 500
  },
  {
    value: 600,
    label: "1000 km"
  }
];
